import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";

export default function ChooseLocations() {
  const [userAreas, setUserAreas] = useState(() => {
    const stored = localStorage.getItem("userAreas");
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedAreas, setSelectedAreas] = useState(() => {
    const stored = localStorage.getItem("selectedAreas");
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch user profile for areas
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axiosInstance.get("/api/v1/auth/profile/");
        const fetchedAreas = res.data.user_areas || [];
        
        // Update areas in state and localStorage
        setUserAreas(fetchedAreas);
        localStorage.setItem("userAreas", JSON.stringify(fetchedAreas));
        
        // Clear selected areas if they're no longer in user areas
        const validSelectedAreas = selectedAreas.filter(area => fetchedAreas.includes(area));
        if (validSelectedAreas.length !== selectedAreas.length) {
          setSelectedAreas(validSelectedAreas);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Could not load profile and areas.");
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Sync selectedAreas with localStorage
  useEffect(() => {
    localStorage.setItem("selectedAreas", JSON.stringify(selectedAreas));
  }, [selectedAreas]);

  const handleCheckboxChange = (area) => {
    localStorage.removeItem("selectedArea");
    setSelectedAreas((prev) =>
      prev.includes(area)
        ? prev.filter((a) => a !== area)
        : [...prev, area]
    );
  };

  const removeArea = (area) => {
    setSelectedAreas((prev) => prev.filter((a) => a !== area));
  };

  return (
    <div className="p-4 max-w-md mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Select Locations</h1>
        <p className="text-sm text-gray-600 mt-1">Choose one or more areas to continue.</p>
      </div>

      {loading ? (
        <div className="text-center text-sm text-gray-500">Loading locations...</div>
      ) : error ? (
        <p className="text-red-600 text-sm">{error}</p>
      ) : userAreas.length > 0 ? (
        <div className="relative" ref={dropdownRef}>
          {/* Selected areas display */}
          <div 
            className="bg-white rounded-lg border border-gray-300 p-3 cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex flex-wrap gap-2">
              {selectedAreas.length > 0 ? (
                selectedAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-customIndigo text-white"
                  >
                    {area}
                    <button
                      type="button"
                      className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-indigo-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeArea(area);
                      }}
                    >
                      <span className="sr-only">Remove {area}</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-gray-500">Select areas...</span>
              )}
            </div>
          </div>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
              <div className="py-1">
                {userAreas.sort().map((area) => (
                  <label
                    key={area}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      value={area}
                      checked={selectedAreas.includes(area)}
                      onChange={() => handleCheckboxChange(area)}
                      className="w-4 h-4 text-customIndigo border-gray-300 rounded focus:ring-customIndigo"
                    />
                    <span className="ml-3 text-sm text-gray-700">{area}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No areas available.</p>
      )}

      <div>
        <Link
          to="/patrolplan"
          className={`w-full block text-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow transition focus:outline-none focus:ring-2 focus:ring-customIndigo ${
            selectedAreas.length > 0 
              ? 'bg-customIndigo hover:bg-indigo-500' 
              : 'bg-gray-300 cursor-not-allowed'
          }`}
          onClick={(e) => {
            if (selectedAreas.length === 0) {
              e.preventDefault();
            }
          }}
        >
          {selectedAreas.length > 0 ? 'Continue' : 'Select at least one area'}
        </Link>
      </div>
    </div>
  );
}
